/** « Tendances TCF Cameroun »: themes most reported by the learners after their exam (last 3 months). */

import { type Trend, useAcademyTrendsList } from "@agencyos/api-client";
import { GlassCard, SelectField, Skeleton } from "@agencyos/ui-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAdminExam, useExamTexts } from "./examContext";

export interface TrendGroup {
  skill: string;
  center: string;
  rows: Trend[];
  max: number;
}

/** By skill then center, the most reported themes first. */
export function groupTrends(rows: Trend[]): TrendGroup[] {
  const groups = new Map<string, TrendGroup>();
  for (const row of rows) {
    const id = `${row.skill}|${row.center}`;
    const group = groups.get(id) ?? { skill: row.skill, center: row.center, rows: [], max: 0 };
    group.rows.push(row);
    group.max = Math.max(group.max, row.count);
    groups.set(id, group);
  }
  return [...groups.values()]
    .map((g) => ({ ...g, rows: [...g.rows].sort((a, b) => b.count - a.count) }))
    .sort((a, b) => a.skill.localeCompare(b.skill) || b.max - a.max || a.center.localeCompare(b.center));
}

export function TrendsTab() {
  const { t } = useTranslation();
  const { contentParams: params } = useAdminExam();
  const texts = useExamTexts();
  const list = useAcademyTrendsList(params);
  const [skill, setSkill] = useState("");
  const rows = list.data?.status === 200 ? list.data.data : undefined;
  const groups = groupTrends((rows ?? []).filter((r) => !skill || r.skill === skill));
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{texts.title("trends", "academyAdmin.trends.title")}</h2>
          <p className="muted">{t("academyAdmin.trends.intro")}</p>
        </div>
      </div>
      <div className="form-grid">
        <SelectField
          label={t("academyAdmin.skillLabel")}
          value={skill}
          options={[
            { value: "", label: t("academyAdmin.all") },
            { value: "ee", label: t("academyAdmin.skill.ee") },
            { value: "eo", label: t("academyAdmin.skill.eo") },
          ]}
          onChange={(e) => setSkill(e.target.value)}
        />
      </div>
      {rows === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : groups.length ? (
        <div className="grid-2">
          {groups.map((g) => (
            <section key={`${g.skill}|${g.center}`} className="installment-row stack">
              <h3>
                {t(`academyAdmin.skill.${g.skill}`, { defaultValue: g.skill.toUpperCase() })} · {g.center || t("academyAdmin.trends.noCenter")}
              </h3>
              <ol className="plain-list stack">
                {g.rows.map((r) => (
                  <li key={r.theme} className="meter">
                    <div className="row between">
                      <span lang="fr">{r.theme}</span>
                      <strong>{t("academyAdmin.trends.reports", { count: r.count })}</strong>
                    </div>
                    <div className="meter__bar" role="presentation">
                      <div className="meter__fill" style={{ transform: `scaleX(${g.max ? r.count / g.max : 0})` }} />
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      ) : (
        <p className="muted">{t("academyAdmin.trends.empty")}</p>
      )}
      <p className="muted">{t("academyAdmin.trends.rule")}</p>
    </GlassCard>
  );
}
